/**
 * Iter 137 · Wave C · Governance → Presentation Mode.
 *
 * Owner + Holding-Owner only. Renders existing components in a
 * distraction-free full-screen slideshow. No new data — every section
 * reuses its canonical page. Exit safely returns to previous route.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Presentation, Maximize2, ShieldAlert } from "lucide-react";
import { usePureH } from "../context/PureHContext";
import { useDataMode } from "../context/DataModeContext";
import ExecutiveDashboard from "./ExecutiveDashboard";
import Briefs from "./Briefs";
import Reports from "./Reports";
import Scorecards from "./Scorecards";
import Decisions from "./Decisions";
import Risks from "./finance/Risks";

type Slide = { key: string; label: string; component: React.ComponentType };
const SLIDES: Slide[] = [
  { key: "dashboard",  label: "Executive Dashboard", component: ExecutiveDashboard },
  { key: "briefs",     label: "Board Briefs",        component: Briefs },
  { key: "reports",    label: "Reports",             component: Reports },
  { key: "scorecards", label: "Scorecards",          component: Scorecards },
  { key: "decisions",  label: "Decisions",           component: Decisions },
  { key: "risks",      label: "Risks",               component: Risks },
];

export default function PresentationMode() {
  const { user } = usePureH();
  const { dataMode } = useDataMode();
  const nav = useNavigate();
  const [idx, setIdx] = useState(0);

  const allowed = useMemo(() => {
    const role = (user?.role || "").toLowerCase();
    return role === "owner" || !!(user as any)?.isHoldingOwner;
  }, [user]);

  // P0-3 — hard-lock while workspace is in demo mode. No bypass.
  const demoLocked = dataMode === "demo";

  useEffect(() => {
    if (!allowed) return;
    const on = (e: KeyboardEvent) => {
      if (e.key === "Escape") { nav(-1); return; }
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") { setIdx((i) => Math.min(i + 1, SLIDES.length - 1)); e.preventDefault(); }
      if (e.key === "ArrowLeft"  || e.key === "PageUp")   { setIdx((i) => Math.max(i - 1, 0)); e.preventDefault(); }
      if (e.key.toLowerCase() === "f") { void document.documentElement.requestFullscreen?.().catch(() => {}); }
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [allowed, nav]);

  if (!allowed) {
    return (
      <div className="p-8 text-center text-slate-600" data-testid="presentation-forbidden">
        <ShieldMsg /> Presentation Mode is available to Owner and Holding-Owner only.
      </div>
    );
  }

  if (demoLocked) {
    return (
      <div
        className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-950 text-white p-8 text-center"
        data-testid="presentation-demo-locked"
      >
        <ShieldAlert size={56} className="text-amber-400 mb-6" />
        <h1 className="text-3xl font-semibold mb-4">Presentation Mode is disabled</h1>
        <p className="max-w-xl text-slate-300 leading-relaxed">
          The workspace currently contains <b>demo data</b>. Presentation Mode
          and the Investor Room are locked to prevent simulated figures from
          appearing in an investor-facing layout.
        </p>
        <p className="max-w-xl text-slate-400 mt-4 text-sm">
          Clear demo data from <b>Settings → Data Mode</b> (Owner only) to enable
          Presentation Mode.
        </p>
        <button
          onClick={() => nav(-1)}
          data-testid="presentation-demo-locked-back"
          className="mt-8 px-4 py-2 rounded bg-slate-800 hover:bg-slate-700 text-sm border border-slate-700"
        >
          ← Back
        </button>
      </div>
    );
  }

  const Slide = SLIDES[idx].component;
  const goFullscreen = () => document.documentElement.requestFullscreen?.().catch(() => {});
  const exit = () => nav(-1);

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-hidden flex flex-col" data-testid="presentation-mode">
      {/* Top control strip */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-900 text-white" data-testid="presentation-controls">
        <div className="flex items-center gap-2 text-sm">
          <Presentation size={14} className="text-amber-400" />
          <span className="font-semibold">Presentation Mode</span>
          <span className="text-slate-400">·</span>
          <select value={SLIDES[idx].key} onChange={(e) => setIdx(SLIDES.findIndex((s) => s.key === e.target.value))} data-testid="presentation-selector" className="bg-slate-800 border border-slate-700 rounded px-2 py-0.5 text-xs">
            {SLIDES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <span className="text-slate-400 text-xs">{idx + 1} / {SLIDES.length}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setIdx((i) => Math.max(i - 1, 0))} disabled={idx === 0} data-testid="presentation-prev" className="p-1.5 hover:bg-slate-800 disabled:opacity-40 rounded"><ChevronLeft size={14} /></button>
          <button onClick={() => setIdx((i) => Math.min(i + 1, SLIDES.length - 1))} disabled={idx === SLIDES.length - 1} data-testid="presentation-next" className="p-1.5 hover:bg-slate-800 disabled:opacity-40 rounded"><ChevronRight size={14} /></button>
          <button onClick={goFullscreen} data-testid="presentation-fullscreen" className="p-1.5 hover:bg-slate-800 rounded"><Maximize2 size={13} /></button>
          <button onClick={exit} data-testid="presentation-exit" className="ml-2 px-2 py-1 bg-slate-800 hover:bg-slate-700 rounded text-xs flex items-center gap-1"><X size={12} /> Exit</button>
        </div>
      </div>
      {/* Slide body — editing controls are visually neutralised via pointer-events off on write actions */}
      <div className="flex-1 overflow-y-auto p-6 presentation-scope" data-testid={`presentation-slide-${SLIDES[idx].key}`}>
        <style>{`
          .presentation-scope button[data-testid*="-new"],
          .presentation-scope button[data-testid*="-edit"],
          .presentation-scope button[data-testid*="-delete"],
          .presentation-scope button[data-testid*="-save"] { display: none !important; }
        `}</style>
        <Slide />
      </div>
    </div>
  );
}

function ShieldMsg() {
  return <span className="inline-block text-red-500 mr-1">⚠︎</span>;
}
